/**
 * Where a transcript offers a branch and where it marks one (ADR-0016).
 * JSX-free like `messageRuns.ts`, so `node --test` can load it.
 */

import type { Message, SessionSummary } from "../types";
import type { Row } from "./messageRuns";

const entryIdOf = (m: Message | undefined): string | undefined => (m as { entryId?: string } | undefined)?.entryId;

function hasToolCall(m: Message): boolean {
	const content = (m as { content?: unknown }).content;
	return Array.isArray(content) && content.some((c) => (c as { type?: string })?.type === "toolCall");
}

/** The messages a row draws: one for a user row, the whole run for a run row. */
function rangeOf(messages: Message[], row: Row, next: Row | undefined): [number, number] {
	return row.kind === "user" ? [row.index, row.index + 1] : [row.index, next?.index ?? messages.length];
}

/**
 * The entry a branch from this row would be cut at, or null where the server would refuse one: a
 * run that ended on a tool call, or a message the transcript has not written yet.
 */
export function branchPointOf(messages: Message[], row: Row, next: Row | undefined): string | null {
	if (row.kind === "user") return entryIdOf(row.message) ?? null;
	const last = messages[rangeOf(messages, row, next)[1] - 1];
	if (!last || last.role !== "assistant" || hasToolCall(last)) return null;
	return entryIdOf(last) ?? null;
}

/** The branches of `parentId` cut inside this row, in transcript order. */
export function forkedAt(messages: Message[], row: Row, next: Row | undefined, sessions: SessionSummary[], parentId: string): SessionSummary[] {
	const [start, end] = rangeOf(messages, row, next);
	const out: SessionSummary[] = [];
	for (const m of messages.slice(start, end)) {
		const id = entryIdOf(m);
		if (id) out.push(...sessions.filter((s) => s.parentId === parentId && s.forkEntryId === id));
	}
	return out;
}

/** How many of a branch's messages came from its parent: the position of the fork entry, or null when it is not in view. */
export function forkPosition(messages: Message[], forkEntryId: string | null | undefined): number | null {
	if (!forkEntryId) return 0;
	const index = messages.findIndex((m) => entryIdOf(m) === forkEntryId);
	return index < 0 ? null : index + 1;
}
